import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  FolderTree, Plus, Edit, Trash2, GripVertical, Eye, EyeOff, 
  FileText, Loader2, AlertCircle, ChevronRight, Save, X, Copy
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Icônes disponibles pour les catégories
const CATEGORY_ICONS = [
  { value: "ship", label: "Bateau", emoji: "🚢" },
  { value: "hotel", label: "Hôtel", emoji: "🏨" },
  { value: "restaurant", label: "Restaurant", emoji: "🍽️" },
  { value: "map", label: "Destination", emoji: "🗺️" },
  { value: "gift", label: "Bon Plan", emoji: "🎁" },
  { value: "star", label: "Test/Avis", emoji: "⭐" },
  { value: "plane", label: "Avion", emoji: "✈️" },
  { value: "train", label: "Train", emoji: "🚂" },
  { value: "car", label: "Voiture", emoji: "🚗" },
  { value: "beach", label: "Plage", emoji: "🏖️" },
  { value: "mountain", label: "Montagne", emoji: "🏔️" },
  { value: "city", label: "Ville", emoji: "🏙️" },
];

export default function CategoriesManagementPage() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    icon: "",
    parent_id: "",
    order: 0,
    is_active: true,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Impossible de charger les catégories");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (name) => {
    setForm({
      ...form,
      name,
      slug: editingCategory ? form.slug : generateSlug(name),
    });
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setForm({
      name: "",
      slug: "",
      description: "",
      icon: "",
      parent_id: "",
      order: categories.length,
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setForm({
      name: category.name,
      slug: category.slug,
      description: category.description || "",
      icon: category.icon || "",
      parent_id: category.parent_id || "",
      order: category.order || 0,
      is_active: category.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error("Le nom de la catégorie est obligatoire");
      return;
    }

    if (!form.slug.trim()) {
      toast.error("L'identifiant (slug) est obligatoire");
      return;
    }

    setSaving(true);

    try {
      if (editingCategory) {
        // Update
        await axios.put(
          `${API}/api/categories/${editingCategory.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Catégorie modifiée");
      } else {
        // Create
        await axios.post(
          `${API}/api/categories`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Catégorie créée");
      }
      
      setShowModal(false);
      loadCategories();
    } catch (error) {
      console.error("Error saving category:", error);
      const message = error.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    try {
      await axios.delete(
        `${API}/api/categories/${category.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Catégorie supprimée");
      setDeleteConfirm(null);
      loadCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      const message = error.response?.data?.detail || "Impossible de supprimer la catégorie";
      toast.error(message);
    }
  };

  const toggleActive = async (category) => {
    try {
      await axios.put(
        `${API}/api/categories/${category.id}`,
        { ...category, is_active: !category.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadCategories();
      toast.success(category.is_active ? "Catégorie désactivée" : "Catégorie activée");
    } catch (error) {
      console.error("Error toggling category:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  const getIconEmoji = (iconValue) => {
    const icon = CATEGORY_ICONS.find(i => i.value === iconValue);
    return icon ? icon.emoji : "📁";
  };

  // Get parent categories (for hierarchy)
  const parentCategories = categories.filter(c => !c.parent_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <FolderTree className="h-6 w-6 text-accent" />
            Catégories
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gérez les catégories de contenu du site
          </p>
        </div>
        <Button onClick={openCreateModal} className="bg-accent hover:bg-accent/90">
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle catégorie
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-primary">{categories.length}</div>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {categories.filter(c => c.is_active !== false).length}
            </div>
            <p className="text-xs text-muted-foreground">Actives</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-gray-400">
              {categories.filter(c => c.is_active === false).length}
            </div>
            <p className="text-xs text-muted-foreground">Inactives</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-accent">
              {categories.reduce((sum, c) => sum + (c.article_count || 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground">Articles</p>
          </CardContent>
        </Card>
      </div>

      {/* Categories List */}
      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderTree className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Aucune catégorie créée</p>
            <Button onClick={openCreateModal} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Créer la première catégorie
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {categories.map((category, index) => (
                <div 
                  key={category.id}
                  className={`flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors ${
                    category.is_active === false ? "opacity-50" : ""
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-muted-foreground cursor-grab">
                      <GripVertical className="h-4 w-4" />
                      <span className="text-sm w-6">{index + 1}</span>
                    </div>
                    <div className="text-2xl">{getIconEmoji(category.icon)}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-primary">{category.name}</h3>
                        {category.is_active === false && (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        /{category.slug}
                        {category.article_count > 0 && (
                          <span className="ml-2">• {category.article_count} article{category.article_count > 1 ? "s" : ""}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground/60 font-mono mt-0.5">
                        ID: {category.id}
                      </p>
                      {category.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                          {category.description}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(category)}
                      title={category.is_active !== false ? "Désactiver" : "Activer"}
                    >
                      {category.is_active !== false ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(category)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirm(category)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? "Modifier la catégorie" : "Nouvelle catégorie"}
            </DialogTitle>
            <DialogDescription>
              {editingCategory 
                ? "Modifiez les informations de la catégorie"
                : "Créez une nouvelle catégorie pour organiser vos contenus"
              }
            </DialogDescription>
            {editingCategory && (
              <div className="mt-2 flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                <span className="text-xs text-muted-foreground">ID :</span>
                <code className="text-xs font-mono bg-muted px-2 py-0.5 rounded select-all">
                  {editingCategory.id}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => {
                    navigator.clipboard.writeText(editingCategory.id);
                    toast.success("ID copié dans le presse-papier");
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ex: Croisières"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Identifiant (slug) *</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="Ex: croisieres"
                required
              />
              <p className="text-xs text-muted-foreground">
                Utilisé dans l'URL : /categorie/{form.slug || "..."}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Description de la catégorie..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icône</Label>
                <Select 
                  value={form.icon || "none"} 
                  onValueChange={(value) => setForm({ ...form, icon: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une icône" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="flex items-center gap-2">
                        <span>📁</span>
                        <span>Par défaut</span>
                      </span>
                    </SelectItem>
                    {CATEGORY_ICONS.map((icon) => (
                      <SelectItem key={icon.value} value={icon.value}>
                        <span className="flex items-center gap-2">
                          <span>{icon.emoji}</span>
                          <span>{icon.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Catégorie parente</Label>
                <Select 
                  value={form.parent_id || "none"} 
                  onValueChange={(value) => setForm({ ...form, parent_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Aucune (racine)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune (racine)</SelectItem>
                    {parentCategories
                      .filter(c => c.id !== editingCategory?.id)
                      .map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {getIconEmoji(cat.icon)} {cat.name}
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="order">Ordre d'affichage</Label>
                <Input
                  id="order"
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>

              <div className="space-y-2">
                <Label>Statut</Label>
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {form.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="bg-accent hover:bg-accent/90">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingCategory ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la catégorie "{deleteConfirm?.name}" ?
              {deleteConfirm?.article_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ Cette catégorie contient {deleteConfirm.article_count} article(s).
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(deleteConfirm)}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
